/**
 * Velocity Flow Diagram
 *
 * Builds and renders a Velocity-styled flow diagram for a single real-time
 * analytic. Unlike the GeoEvent diagram (green/blue/yellow shapes that mirror
 * the GeoEvent designer), this view recreates the look of the ArcGIS Velocity
 * model editor: white rounded-rectangle nodes with a thin teal accent, a
 * component icon, the Velocity label, and teal connector links.
 *
 * The graph is derived from the generated Velocity configuration (feeds,
 * sources, tools, outputs + pipeline connectivity), so it shows the user
 * exactly how to wire the equivalent model in Velocity rather than how the
 * source GeoEvent service was arranged.
 *
 * Geometry is delegated to the shared {@link ./flow-layout.ts} layered layout
 * so the Velocity and GeoEvent diagrams stay structurally consistent; only the
 * node sizing and styling differ.
 */

import type { VelocityModel } from './velocity-model.js';
import type {
  VelocityRealtimeAnalytic,
  VelocityFlowNode,
  VelocityFlowEdge,
  VelocityFlowGraph,
  VelocityFlowNodeKind,
  VelocityIconMap,
} from '../types/velocity.js';
import { computeLayeredLayout, type PositionedNode } from './flow-layout.js';
import { sanitizeVelocityLabel } from '../generator/velocity-label.js';

export type {
  VelocityFlowNode,
  VelocityFlowEdge,
  VelocityFlowGraph,
  VelocityFlowNodeKind,
  VelocityIconEntry,
  VelocityIconMap,
} from '../types/velocity.js';

/**
 * Builds the Velocity flow graph for an analytic from the generated
 * configuration. Nodes come from the analytic's feeds, sources, tools, and
 * outputs; edges come from the pipeline connections (one edge per `from → to`
 * pairing). Edges referencing an unknown endpoint are skipped defensively.
 */
export function buildVelocityFlowGraph(
  model: VelocityModel,
  analytic: VelocityRealtimeAnalytic,
): VelocityFlowGraph {
  const nodes: VelocityFlowNode[] = [];
  const seen = new Set<string>();

  const add = (node: VelocityFlowNode): void => {
    if (!node.id || seen.has(node.id)) return;
    seen.add(node.id);
    nodes.push(node);
  };

  for (const feedRef of analytic.feeds || []) {
    const feedItem = model.feedsById.get(feedRef.id);
    add({
      id: feedRef.id,
      label: feedRef.label || feedItem?.label || feedRef.name || 'Feed',
      kind: 'feed',
      velocityName: feedItem?.feed?.name || feedRef.name || 'feed',
    });
  }
  for (const source of analytic.sources || []) {
    add({
      id: source.id,
      label: source.label || source.name || 'Data Source',
      kind: 'source',
      velocityName: source.name || 'data-source',
    });
  }
  for (const tool of analytic.tools || []) {
    add({
      id: tool.id,
      label: tool.label || tool.name || 'Tool',
      kind: 'tool',
      velocityName: tool.name || 'tool',
    });
  }
  for (const output of analytic.outputs || []) {
    add({
      id: output.id,
      label: output.label || output.name || 'Output',
      kind: 'output',
      velocityName: output.name || 'output',
    });
  }

  const edges: VelocityFlowEdge[] = [];
  const edgeSeen = new Set<string>();
  for (const conn of analytic.pipeline || []) {
    const fromId = conn.from?.id;
    if (!fromId || !seen.has(fromId)) continue;
    for (const to of conn.tos || []) {
      const toId = to?.id;
      if (!toId || !seen.has(toId)) continue;
      const key = `${fromId}\u0000${toId}`;
      if (edgeSeen.has(key)) continue;
      edgeSeen.add(key);
      edges.push({ from: fromId, to: toId });
    }
  }

  return { nodes, edges };
}

/**
 * Builds a Velocity-side neighborhood subgraph centered on the Velocity
 * component that corresponds to a given GeoEvent component, mirroring the
 * GeoEvent `buildNeighborhoodDiagram` behaviour: the centered node plus its
 * direct predecessors and successors. Correlation is by sanitized label (the
 * same scheme the generator uses to name Velocity components from GeoEvent
 * ones). Returns `undefined` when there is no matching Velocity node, when the
 * full graph is too small to be worth a neighborhood view (<= 3 nodes), or when
 * the neighborhood would be the entire graph (the overview already shows it).
 */
export function buildVelocityNeighborhoodGraph(
  model: VelocityModel,
  analytic: VelocityRealtimeAnalytic,
  geoEventLabel: string,
): { graph: VelocityFlowGraph; highlightNodeId: string } | undefined {
  const full = buildVelocityFlowGraph(model, analytic);
  if (full.nodes.length <= 3) return undefined;

  const target = sanitizeVelocityLabel(geoEventLabel).sanitized;
  const center = full.nodes.find((n) => n.label === target);
  if (!center) return undefined;

  const predecessors = new Set<string>();
  const successors = new Set<string>();
  for (const edge of full.edges) {
    if (edge.to === center.id) predecessors.add(edge.from);
    if (edge.from === center.id) successors.add(edge.to);
  }

  const neighborIds = new Set<string>([center.id, ...predecessors, ...successors]);
  if (neighborIds.size >= full.nodes.length) return undefined;

  const nodes = full.nodes.filter((n) => neighborIds.has(n.id));
  const edges = full.edges.filter((e) => neighborIds.has(e.from) && neighborIds.has(e.to));
  return { graph: { nodes, edges }, highlightNodeId: center.id };
}

// --- Rendering --------------------------------------------------------------

/** Velocity node geometry — larger than GeoEvent boxes to fit icon + label. */
const VEL_NODE_HEIGHT = 64;
const VEL_NODE_MIN_WIDTH = 190;
const VEL_NODE_MAX_WIDTH = 320;
const VEL_V_GAP = 36;
const VEL_H_GAP = 64;
const VEL_PADDING = 24;
const VEL_FONT_SIZE = 12;
const VEL_CHAR_WIDTH = 6.7;

/**
 * Per-kind corner radius (px). Velocity uses subtly different rounding to
 * distinguish node types: tools have noticeably sharper corners, while feeds
 * and outputs are a touch more rounded than a generic box. Sources track the
 * feed/output rounding.
 */
const VEL_NODE_RADIUS: Record<VelocityFlowNodeKind, number> = {
  feed: 12,
  source: 12,
  tool: 4,
  output: 12,
};

/** Icon gutter geometry inside a node. */
const ICON_LEFT_PAD = 16;
const ICON_SIZE = 28;
const ICON_TEXT_GAP = 12;
const TEXT_RIGHT_PAD = 18;
const ICON_GUTTER = ICON_LEFT_PAD + ICON_SIZE + ICON_TEXT_GAP;

/** Brand palette for the Velocity look. */
const VEL_BG = '#FFFFFF';
const VEL_BORDER = '#D4D4D4';
const VEL_LINK = '#07CDCA';
const VEL_TEXT = '#323232';
// Red flags components that need manual work in the service diagram.
const VEL_HIGHLIGHT = '#DC143C';
// Blue marks the focus node in a "Node Neighbor(s)" diagram. Deliberately not
// red so it doesn't read as a problem with that component.
const VEL_NEIGHBOR_HIGHLIGHT = '#0079C1';

/** Per-kind accent (left bar + icon fallback disc) — kept in the teal family. */
const KIND_ACCENT: Record<VelocityFlowNodeKind, string> = {
  feed: '#007472',
  source: '#07CDCA',
  tool: '#00A0A0',
  output: '#005A58',
};

/** Maximum number of characters shown in a node label before truncation. */
const MAX_LABEL_CHARS = 34;

/** Options that customise the emitted Velocity SVG. */
export interface VelocitySvgRenderOptions {
  /** Highlight the neighborhood focus node with a blue outline. */
  highlightNodeId?: string;
  /**
   * Node IDs to outline in red to flag that they need manual configuration.
   * Independent of {@link highlightNodeId} (which marks a neighborhood center).
   */
  manualNodeIds?: Set<string>;
  /** Map of node id → anchor id for click-to-jump navigation. */
  anchorMap?: Record<string, string>;
  /** Stable id prefix so multiple inline SVGs on one page never collide. */
  idPrefix?: string;
  /** Vendored Velocity icons keyed by component name. */
  iconMap?: VelocityIconMap;
}

/** Result of a Velocity SVG render. */
export interface VelocitySvgRenderResult {
  svg: string;
  width: number;
  height: number;
  nodeCount: number;
}

function measureVelocityWidth(label: string): number {
  const display = truncateLabel(label);
  const labelWidth = display.length * VEL_CHAR_WIDTH;
  const total = ICON_GUTTER + labelWidth + TEXT_RIGHT_PAD;
  return Math.min(VEL_NODE_MAX_WIDTH, Math.max(VEL_NODE_MIN_WIDTH, total));
}

/**
 * Renders the Velocity flow graph as an inline SVG string. Returns `null`
 * when the graph has no nodes so callers can fall back to a textual summary.
 */
export function renderVelocityFlowSvg(
  graph: VelocityFlowGraph,
  options: VelocitySvgRenderOptions = {},
): VelocitySvgRenderResult | null {
  const layout = computeLayeredLayout(graph.nodes, graph.edges, {
    nodeHeight: VEL_NODE_HEIGHT,
    vGap: VEL_V_GAP,
    hGap: VEL_H_GAP,
    minWidth: VEL_NODE_MIN_WIDTH,
    padding: VEL_PADDING,
    measureWidth: measureVelocityWidth,
  });
  if (layout.length === 0) return null;

  const dims = computeVelocityDimensions(layout);
  const width = dims.width;
  const height = dims.height;
  const idPrefix = options.idPrefix ?? `vmavel-${Math.random().toString(36).slice(2, 8)}`;
  const arrowMarkerId = `${idPrefix}-arrow`;

  const nodeMap = new Map<string, PositionedNode<VelocityFlowNode>>();
  for (const ln of layout) nodeMap.set(ln.node.id, ln);

  // --- Edges (orthogonal-ish teal links) ---
  const edgeParts: string[] = [];
  for (const edge of graph.edges) {
    const from = nodeMap.get(edge.from);
    const to = nodeMap.get(edge.to);
    if (!from || !to) continue;
    const x1 = from.x + from.width;
    const y1 = from.y + VEL_NODE_HEIGHT / 2;
    const x2 = to.x;
    const y2 = to.y + VEL_NODE_HEIGHT / 2;
    const midX = x1 + (x2 - x1) / 2;
    const d = `M${fmt(x1)},${fmt(y1)} C${fmt(midX)},${fmt(y1)} ${fmt(midX)},${fmt(y2)} ${fmt(x2)},${fmt(y2)}`;
    edgeParts.push(
      `<path class="vma-vel__edge" d="${d}" fill="none" stroke="${VEL_LINK}" stroke-width="3" marker-end="url(#${arrowMarkerId})"></path>`,
    );
  }

  // --- Nodes ---
  const nodeParts: string[] = [];
  for (const ln of layout) {
    const node = ln.node;
    const accent = KIND_ACCENT[node.kind];
    const x = ln.x;
    const y = ln.y;
    const w = ln.width;
    const isHighlighted = options.highlightNodeId && node.id === options.highlightNodeId;
    const isManual = options.manualNodeIds ? options.manualNodeIds.has(node.id) : false;
    const anchorId = options.anchorMap?.[node.id];
    const radius = VEL_NODE_RADIUS[node.kind];

    const box = `<rect x="${fmt(x)}" y="${fmt(y)}" width="${fmt(w)}" height="${fmt(VEL_NODE_HEIGHT)}" rx="${radius}" ry="${radius}" fill="${VEL_BG}" stroke="${VEL_BORDER}" stroke-width="1.5"></rect>`;

    const iconX = x + ICON_LEFT_PAD;
    const iconY = y + (VEL_NODE_HEIGHT - ICON_SIZE) / 2;
    const icon = renderIcon(options.iconMap, node.velocityName, iconX, iconY, accent);

    const textX = x + ICON_GUTTER;
    const textY = y + VEL_NODE_HEIGHT / 2;
    const display = truncateLabel(node.label);
    const kindText = velocityKindShort(node.kind);
    const label = `<text x="${fmt(textX)}" y="${fmt(textY - 6)}" text-anchor="start" dominant-baseline="central" font-family="'Avenir Next', Arial, Helvetica, sans-serif" font-size="${VEL_FONT_SIZE}" font-weight="600" fill="${VEL_TEXT}" pointer-events="none">${escapeXml(display)}</text>`;
    const kind = `<text x="${fmt(textX)}" y="${fmt(textY + 10)}" text-anchor="start" dominant-baseline="central" font-family="'Avenir Next', Arial, Helvetica, sans-serif" font-size="9" letter-spacing="0.5" fill="${accent}" pointer-events="none">${escapeXml(kindText)}</text>`;

    const highlight = isHighlighted
      ? `<rect class="vma-vel__node-highlight" x="${fmt(x - 3)}" y="${fmt(y - 3)}" width="${fmt(w + 6)}" height="${fmt(VEL_NODE_HEIGHT + 6)}" rx="${radius + 3}" ry="${radius + 3}" fill="none" stroke="${VEL_NEIGHBOR_HIGHLIGHT}" stroke-width="3"></rect>`
      : '';

    // Red outline flagging a node that needs manual configuration. Rendered
    // independently of the neighborhood-center highlight above.
    const manual = isManual
      ? `<rect class="vma-vel__node-manual" x="${fmt(x - 3)}" y="${fmt(y - 3)}" width="${fmt(w + 6)}" height="${fmt(VEL_NODE_HEIGHT + 6)}" rx="${radius + 3}" ry="${radius + 3}" fill="none" stroke="${VEL_HIGHLIGHT}" stroke-width="3"></rect>`
      : '';

    const dataAttrs = [
      `data-id="${escapeAttr(node.id)}"`,
      `data-kind="${escapeAttr(node.kind)}"`,
    ];
    if (anchorId) dataAttrs.push(`data-anchor="${escapeAttr(anchorId)}"`);
    if (isManual) dataAttrs.push('data-manual="true"');

    const accessible = anchorId
      ? ` role="link" tabindex="0" aria-label="${escapeAttr(`${node.label} (jump to details)`)}"`
      : ` aria-label="${escapeAttr(node.label)}"`;

    nodeParts.push(
      `<g class="vma-vel__node${anchorId ? ' is-clickable' : ''}" ${dataAttrs.join(' ')}${accessible}>${box}${icon}${label}${kind}${highlight}${manual}<title>${escapeXml(node.label)}</title></g>`,
    );
  }

  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" class="vma-vel__svg" viewBox="0 0 ${fmt(width)} ${fmt(height)}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Velocity model diagram with ${graph.nodes.length} nodes">`,
    `<defs>`,
    `<marker id="${arrowMarkerId}" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">`,
    `<path d="M0,0 L9,5 L0,10 z" fill="${VEL_LINK}"></path>`,
    `</marker>`,
    `</defs>`,
    `<g class="vma-vel__viewport" transform="translate(0,0) scale(1)">`,
    ...edgeParts,
    ...nodeParts,
    `</g>`,
    `</svg>`,
  ].join('');

  return { svg, width, height, nodeCount: graph.nodes.length };
}

/** Total drawing dimensions for a Velocity layout, with a legend floor. */
function computeVelocityDimensions(
  layout: Array<PositionedNode<VelocityFlowNode>>,
): { width: number; height: number } {
  if (layout.length === 0) return { width: 240, height: 90 };
  let maxX = 0;
  let maxY = 0;
  for (const ln of layout) {
    maxX = Math.max(maxX, ln.x + ln.width);
    maxY = Math.max(maxY, ln.y + VEL_NODE_HEIGHT);
  }
  const LEGEND_MIN_WIDTH = 360;
  return {
    width: Math.max(maxX + VEL_PADDING, LEGEND_MIN_WIDTH),
    height: maxY + VEL_PADDING,
  };
}

/** Renders the component icon, or a kind-colored fallback disc. */
function renderIcon(
  iconMap: VelocityIconMap | undefined,
  velocityName: string,
  x: number,
  y: number,
  accent: string,
): string {
  const entry = iconMap?.[velocityName];
  if (entry && entry.body) {
    return `<svg x="${fmt(x)}" y="${fmt(y)}" width="${ICON_SIZE}" height="${ICON_SIZE}" viewBox="${escapeAttr(entry.viewBox || '0 0 24 24')}" preserveAspectRatio="xMidYMid meet">${entry.body}</svg>`;
  }
  const r = ICON_SIZE / 2;
  return `<circle cx="${fmt(x + r)}" cy="${fmt(y + r)}" r="${fmt(r)}" fill="${accent}" opacity="0.18"></circle><circle cx="${fmt(x + r)}" cy="${fmt(y + r)}" r="${fmt(r / 2.6)}" fill="${accent}"></circle>`;
}

/** Truncates a label for display, keeping the full text in the node title. */
function truncateLabel(label: string): string {
  if (label.length <= MAX_LABEL_CHARS) return label;
  return `${label.slice(0, MAX_LABEL_CHARS - 1).trimEnd()}\u2026`;
}

/** Short uppercase tag shown under the label. */
function velocityKindShort(kind: VelocityFlowNodeKind): string {
  switch (kind) {
    case 'feed':
      return 'FEED';
    case 'source':
      return 'DATA SOURCE';
    case 'tool':
      return 'TOOL';
    case 'output':
      return 'OUTPUT';
    default:
      return '';
  }
}

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : (Math.round(n * 100) / 100).toString();
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
